/**
 * App Provider
 *
 * Root provider that combines all context providers in the correct order
 * Provides a single entry point for all application-level providers
 */

import { PropsWithChildren, ReactElement, createElement } from 'react';
import { Provider as JotaiProvider } from 'jotai';
import { ErrorBoundary } from '@/components/error/ErrorBoundary';
import { QueryProvider } from './QueryProvider';
import { AuthProvider } from './AuthProvider';
import { ThemeProvider } from './ThemeProvider';
import { WebSocketProvider } from './WebSocketProvider';
import { I18nProvider } from './I18nProvider';

// App provider props
interface AppProviderProps extends PropsWithChildren {
  // Allow disabling specific providers for testing
  enableAuth?: boolean;
  enableWebSocket?: boolean;
  enableTheme?: boolean;
  enableI18n?: boolean;
}

// Provider configuration type
type ProviderConfig = {
  component: React.ComponentType<PropsWithChildren>;
  enabled: boolean;
  name: string;
};

// Provider builder class
class ProviderBuilder {
  private providers: ProviderConfig[] = [];
  private children: React.ReactNode;

  constructor(children: React.ReactNode) {
    this.children = children;
  }

  addProvider(
    component: React.ComponentType<PropsWithChildren>,
    enabled: boolean,
    name: string
  ): this {
    this.providers.push({ component, enabled, name });
    return this;
  }

  compose(): ReactElement {
    // Start with children and wrap from inside-out
    let result: React.ReactNode = this.children;

    for (let i = 0; i < this.providers.length; i++) {
      const { component, enabled, name } = this.providers[i];
      if (enabled) {
        result = createElement(component, { key: name }, result);
      }
    }

    return <>{result}</>;
  }
}

/**
 * Provider Hierarchy:
 *
 * ErrorBoundary
 * └── JotaiProvider
 *     └── QueryProvider
 *         └── ThemeProvider (if enabled)
 *             └── AuthProvider (if enabled)
 *                 └── I18nProvider (if enabled)
 *                     └── WebSocketProvider (if enabled)
 *                         └── {children}
 */
export function AppProvider({
  children,
  enableAuth = true,
  enableWebSocket = true,
  enableTheme = true,
  enableI18n = true,
}: AppProviderProps): ReactElement {
  return (
    <ErrorBoundary>
      {/* Jotai Provider - Provides atom store */}
      <JotaiProvider>
        {/* TanStack Query Provider - Provides query client */}
        <QueryProvider>
          {new ProviderBuilder(children)
            /* WebSocket Provider - Manages real-time connections */
            .addProvider(WebSocketProvider, enableWebSocket, 'webSocket')
            /* I18n Provider - Manages internationalization */
            .addProvider(I18nProvider, enableI18n, 'i18n')
            /* Auth Provider - Manages authentication state */
            .addProvider(AuthProvider, enableAuth, 'auth')
            /* Theme Provider - Manages theme and accessibility */
            .addProvider(ThemeProvider, enableTheme, 'theme')
            .compose()}
        </QueryProvider>
      </JotaiProvider>
    </ErrorBoundary>
  );
}

// Convenience exports for testing
export const TestAppProvider = ({ children }: PropsWithChildren): ReactElement => (
  <AppProvider enableAuth={false} enableWebSocket={false} enableTheme={false}>
    {children}
  </AppProvider>
);

export const AuthTestProvider = ({ children }: PropsWithChildren): ReactElement => (
  <AppProvider enableAuth={true} enableWebSocket={false} enableTheme={false}>
    {children}
  </AppProvider>
);

export const FullTestProvider = ({ children }: PropsWithChildren): ReactElement => (
  <AppProvider enableAuth={true} enableWebSocket={true} enableTheme={true}>
    {children}
  </AppProvider>
);
